import React from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import { DefaultPage } from '../pages/DefaultPage';
import { Dashboard } from '../entities/Dashboard';
import { Magazine } from '../entities/Magazine';
import { MagazineRead } from '../entities/MagazineRead';
import t from '../../utils/labels';
import { AuthRoutes } from '@food/auth/build/dist/components/AuthRoutes';
import { AuthContext } from '@food/auth/build/dist/components/AuthContext';
import { ScrollToTop } from '../ScrollToTop';
import { Router } from 'react-router-dom';
import { history } from '../../utils/history';
import { ApolloProvider } from 'react-apollo';
import { cache, httpLink } from '../../utils/client';
import { NewsStandArgs } from '../../types';
import { PreviewRead } from '../entities/PreviewRead';
import { UserRole } from '../../server-types';
import { authConfigDefault } from '../../utils/config';
import { RedirectHandler } from '@food/auth/build/dist/types';
import { auth } from '@food/auth/build/dist/logic/auth';
import { Channel } from '@food/sync';
import * as Sentry from '@sentry/browser';
import { stacked } from '../../utils/broadcastChannel';
import { ApolloLink } from 'apollo-link';
import { targetSiteLink } from '@food/auth/build/dist/links/targetSiteLink';
import { RenewLink } from '@food/auth/build/dist/links/RenewLink';
import ApolloClient from 'apollo-client';
import { checkForAchievements, checkForAskAuth } from '../../utils/misc';
import { parse } from 'query-string';
import { AnalyticsListener } from '../AnalyticsListener';

export const authConfig = {
	...authConfigDefault,
	AUTH_ROLES: [UserRole.Consumer, UserRole.Producer],
};

export const onRedirect: RedirectHandler = (_, redirectUrl) => {
	history.replace(redirectUrl);
};
export const authManager = auth(authConfig);
const authChannel = new Channel('@food/auth');

const userChangedHandler: (
	oldUser: {
		id: string;
		name: string;
		email: string;
	} | null,
	newUser: {
		id: string;
		name: string;
		email: string;
	} | null,
) => Promise<void> = async (
	_,
	newUser: {
		id: string;
		name: string;
		email: string;
	} | null,
	) => {
		const { location } = history;
		const searchParams = parse(location.search);

		Sentry.configureScope((scope) => {
			if (newUser) {
				scope.setUser({
					id: newUser.id,
					username: newUser.name,
					email: newUser.email,
				});
			} else {
				scope.setUser(null);
			}
		});

		//await client.clearStore();
		//await client.cache.reset();
		// client.reFetchObservableQueries();

		await client.cache.reset();
		client.resetStore();

		const redirectUrl = searchParams.redirect;
		if (redirectUrl) {
			onRedirect(newUser, redirectUrl as string);
		} else if (location.pathname === '/login') {
			onRedirect(newUser, '/');
		}
	};

authChannel.onmessage = stacked((events: any[]) => {
	const lastEvent = events.filter((e) => e.data.type === 'USER_CHANGED')[0];

	if (lastEvent) {
		const { oldUser, newUser } = lastEvent.data.args;
		userChangedHandler(oldUser, newUser);
	}
}, 100);

const link = ApolloLink.from([
	new (targetSiteLink(authConfig))(),
	new (RenewLink(authManager))(),
	httpLink,
]);

export const client = new ApolloClient({
	link,
	cache,
});

const DashboardPage = ({ match: { params } }: any) => {
	checkForAskAuth();

	return <DefaultPage>{(args) => <Dashboard {...args} filter1={params.filter1} filter2={params.filter2} />}</DefaultPage>;
};

const MagazinePage = ({ match: { params } }: any) => {
	checkForAskAuth();

	return (
		<DefaultPage>
			{(args) => <Magazine {...args} magazineId={params.slug} />}
		</DefaultPage>
	);
};

const MagazineReadPage = ({ match: { params } }: { match: any }) => {
	checkForAskAuth();

	return (
		<DefaultPage>
			{(args) => <MagazineRead {...args} magazineId={params.slug} />}
		</DefaultPage>
	);
};

const MagazinePreviewPage = ({ match: { params } }: { match: any }) => (
	<DefaultPage>
		{(args) => <PreviewRead {...args} magazineId={params.slug} />}
	</DefaultPage>
);

interface IProps extends WithTranslation {
	args: NewsStandArgs;
}

const authContainer: React.FC<any> = ({ children }) => <div>{children}</div>;

const InitialApp: React.FC<IProps> = () => {
	checkForAchievements();

	return (
		<Router history={history}>
			<ApolloProvider client={client}>
				<AnalyticsListener />
				<AuthRoutes
					config={authConfig}
					authManager={authManager}
					t={t}
					onRedirect={onRedirect}
					container={authContainer}
				>
					<ScrollToTop>
						<AuthContext>
							<Switch>
								<Route path={'/'} exact component={DashboardPage} />
								<Route path={'/f/:filter1'} exact component={DashboardPage} />
								<Route path={'/f/:filter1/:filter2'} exact component={DashboardPage} />
								<Route path={'/magazine/:slug'} exact component={MagazinePage} />
								<Route
									path={'/magazine/:slug/read'}
									exact
									component={MagazineReadPage}
								/>
								<Route
									path={'/magazine/:slug/preview'}
									exact
									component={MagazinePreviewPage}
								/>
								<Redirect to={'/'} />
							</Switch>
						</AuthContext>
					</ScrollToTop>
				</AuthRoutes>
			</ApolloProvider>
		</Router>
	);
};

const NewsStand = withTranslation('translations')(InitialApp);

export default NewsStand;
